import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, XCircle, CircleDashed } from "lucide-react";

export const ValidateInput = ({
  value = "",
  onChange = () => {},
  autoComplete = "off",
  onValidationChange = () => {},
  validator = null,

  placeholder = "Enter value",
  type = "text",
  minLength = 3,
  debounceTime = 500,
  inputClassName = "",

  icon: Icon = CircleDashed,

  successMessage = "Valid",
  errorMessage = "Already exists",

  initialValue = "",
  isEdit = false,

  disabled = false,
}) => {
  const [input, setInput] = useState(value ?? "");
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [message, setMessage] = useState("");

  const timeoutRef = useRef(null);
  const abortRef = useRef(0); // prevents race conditions

  useEffect(() => {
    setInput(value ?? "");
  }, [value]);

  const validateEmailFormat = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const checkInput = async (val, requestId) => {
    const trimmedValue = String(val || "").trim();
    const originalValue = String(initialValue || "").trim();

    // Empty
    if (!trimmedValue || trimmedValue.length < minLength) {
      onValidationChange({
        value: trimmedValue,
        isValid: null,
        message: "",
      });

      setLoading(false);
      setIsValid(null);
      setMessage("");

      return;
    }

    // Email format validation
    if (type === "email" && !validateEmailFormat(trimmedValue)) {
      onValidationChange({
        value: trimmedValue,
        isValid: false,
        message: "Invalid email format",
      });

      setLoading(false);
      setIsValid(false);
      setMessage("Invalid email format");

      return;
    }

    // EDIT MODE: skip validation if value unchanged
    if (
      isEdit &&
      originalValue &&
      trimmedValue.toLowerCase() === originalValue.toLowerCase()
    ) {
      onValidationChange({
        value: trimmedValue,
        isValid: true,
        message: "",
        skipped: true,
      });

      setLoading(false);
      setIsValid(true);
      setMessage("");

      return;
    }

    if (!validator) {
      setLoading(false);
      setIsValid(null);
      setMessage("");

      return;
    }

    try {
      setLoading(true);

      const result = await validator(trimmedValue);

      if (abortRef.current !== requestId) return;

      const valid = Boolean(result?.valid);

      onValidationChange({
        value: trimmedValue,
        isValid: valid,
        message: result?.message || (valid ? successMessage : errorMessage),
      });

      setIsValid(valid);
      setMessage(result?.message || (valid ? successMessage : errorMessage));
    } catch (err) {
      if (abortRef.current !== requestId) return;

      onValidationChange({
        value: trimmedValue,
        isValid: false,
        message: err?.message || "Validation failed",
      });

      setIsValid(false);
      setMessage(err?.message || "Validation failed");
    } finally {
      if (abortRef.current === requestId) {
        setLoading(false);
      }
    }
  };
  const handleChange = (val) => {
    setInput(val);
    onChange(val);

    setMessage("");
    setIsValid(null);

    clearTimeout(timeoutRef.current);

    const requestId = ++abortRef.current;

    timeoutRef.current = setTimeout(() => {
      checkInput(val, requestId);
    }, debounceTime);
  };

  return (
    <div className="w-full">
      {/* INPUT */}
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        )}
        <input
          type={type}
          value={input ?? ""}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => handleChange(e.target.value)}
          autoComplete={autoComplete}
          className={`
                        w-full rounded-xl
                        border border-slate-300 dark:border-slate-700
                        bg-white dark:bg-slate-800
                         ${Icon ? "pl-10" : "pl-3"}
                        px-3 py-2.5 pr-10
                        text-sm text-slate-900 dark:text-white
                        focus:outline-none
                        focus:ring-2 focus:ring-blue-500
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${inputClassName}
                    `}
        />

        {/* ICON */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          ) : isValid === true ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          ) : isValid === false ? (
            <XCircle className="w-4 h-4 text-red-500" />
          ) : null}
        </div>
      </div>

      {/* INLINE MESSAGE (NEW UX) */}
      {message && (
        <p
          className={`mt-1 text-xs flex items-center gap-1
                    ${isValid ? "text-emerald-600" : "text-red-500"}`}
        >
          {/* {isValid ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                        <XCircle className="w-3.5 h-3.5" />
                    )} */}

          {message}
        </p>
      )}
    </div>
  );
};
